import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Calendar, MapPin, Clock, ArrowRight, Users, Music,
  Loader2, CheckCircle, HelpCircle, Star, X, Plus, Edit3,
  Trash2, Lock, Save, ChevronDown, ChevronUp, Image, Upload
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_MAP = {
  confirmed: { label: 'Confirme', cls: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40' },
  a_confirmer: { label: 'A confirmer', cls: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/40' },
  passe: { label: 'Passe', cls: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/40' }
};

/* ─── Flyer Fullscreen Modal ─── */
function FlyerModal({ image, name, onClose }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 cursor-pointer"
      onClick={onClose}>
      <motion.div initial={{ scale: 0.8 }} animate={{ scale: 1 }} exit={{ scale: 0.8 }}
        className="relative max-w-2xl max-h-[85vh] w-full" onClick={e => e.stopPropagation()}>
        <img src={image} alt={name} className="w-full h-full object-contain rounded-xl shadow-2xl" />
        <button onClick={onClose}
          className="absolute top-3 right-3 p-2 bg-black/60 rounded-full text-white hover:bg-red-500 transition-colors">
          <X size={20} />
        </button>
        <p className="text-center text-white font-bold mt-3 text-lg">{name}</p>
      </motion.div>
    </motion.div>
  );
}

function EventCard({ event, isUpcoming, onFlyerClick, isAdmin, onEdit, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const hasImage = !!event.image;
  const dates = event.dates || [];

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      data-testid={`event-card-${event.event_id || event.id}`}
      className="relative rounded-2xl overflow-hidden border border-white/10 hover:border-yellow-500/30 transition-all group cursor-pointer"
      onClick={() => setExpanded(!expanded)}>

      {/* Flyer as background - click to enlarge */}
      {hasImage && (
        <>
          <img src={event.image} alt={event.name} className="absolute inset-0 w-full h-full object-cover cursor-zoom-in"
            onClick={(e) => { e.stopPropagation(); onFlyerClick(event); }} />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/30 pointer-events-none" />
        </>
      )}
      {!hasImage && <div className="absolute inset-0 bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-900" />}

      {/* Top accent bar */}
      <div className={`absolute top-0 left-0 w-full h-1 bg-gradient-to-r ${event.color || 'from-yellow-500 to-red-500'}`} />

      {/* Badge + Admin buttons */}
      <div className="absolute top-3 right-3 z-10 flex gap-1">
        {isAdmin && (
          <>
            <button onClick={(e) => { e.stopPropagation(); onEdit(event); }}
              className="p-1.5 rounded-lg bg-black/60 text-white hover:bg-orange-500 transition-colors"><Edit3 size={12} /></button>
            <button onClick={(e) => { e.stopPropagation(); onDelete(event.id || event.event_id); }}
              className="p-1.5 rounded-lg bg-black/60 text-white hover:bg-red-500 transition-colors"><Trash2 size={12} /></button>
          </>
        )}
        {isUpcoming && (
          <span className="px-2.5 py-1 rounded-full bg-green-500 text-black text-[10px] font-black uppercase flex items-center gap-1">
            <Star size={10} /> A venir
          </span>
        )}
      </div>

      {/* Content */}
      <div className="relative z-10 p-5" style={{ minHeight: hasImage ? '220px' : '160px' }}>
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <h3 className="text-2xl font-black text-white drop-shadow-lg mb-1">{event.name}</h3>
            <p className="text-sm font-bold text-yellow-400">{event.type}</p>
            {event.dj && (
              <p className="text-xs text-zinc-300 mt-2 flex items-center gap-1">
                <Users size={12} className="text-red-400" /> {event.dj}
              </p>
            )}
          </div>
          {/* Flyer thumbnail - click to enlarge */}
          {hasImage && (
            <div className="flex-shrink-0 relative">
              <img src={event.image} alt="" 
                className="w-20 h-20 rounded-xl object-cover border-2 border-yellow-500/40 shadow-lg" />
              <button onClick={(e) => { e.stopPropagation(); onFlyerClick(event); }}
                className="absolute inset-0 w-full h-full flex items-center justify-center bg-black/0 hover:bg-black/40 rounded-xl transition-colors group/zoom">
                <span className="opacity-0 group-hover/zoom:opacity-100 text-white text-[10px] font-bold bg-black/60 px-2 py-0.5 rounded">ZOOM</span>
              </button>
            </div>
          )}
        </div>

        {/* Description */}
        {event.description && (
          <p className="text-xs text-zinc-300 mt-3 line-clamp-2">{event.description}</p>
        )}

        {/* Next date preview */}
        {dates.length > 0 && (
          <div className="mt-3 flex items-center gap-2 text-xs">
            <Calendar size={12} className="text-yellow-400" />
            <span className="text-white font-semibold">
              {dates[0].date ? formatDateShort(dates[0].date) : dates[0].time || ''}
            </span>
            {dates[0].venue && (
              <span className="text-zinc-400 flex items-center gap-1">
                <MapPin size={10} /> {dates[0].venue}{dates[0].city ? `, ${dates[0].city}` : ''}
              </span>
            )}
            {dates.length > 1 && <span className="text-zinc-500">+{dates.length - 1} dates</span>}
          </div>
        )}
      </div>

      {/* Expanded dates */}
      <AnimatePresence>
        {expanded && dates.length > 0 && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            className="relative z-10 border-t border-white/10 bg-black/80 backdrop-blur-sm">
            <div className="p-4 space-y-2">
              {dates.map((d, i) => {
                const status = STATUS_MAP[d.status] || STATUS_MAP.a_confirmer;
                return (
                  <div key={i} className="flex items-center justify-between gap-3 p-2 rounded-lg bg-white/5">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      {d.date && (
                        <div className="text-center flex-shrink-0 w-12">
                          <p className="text-lg font-black text-yellow-400">{new Date(d.date + 'T00:00:00').getDate()}</p>
                          <p className="text-[9px] text-zinc-400 uppercase">{new Date(d.date + 'T00:00:00').toLocaleDateString('fr-FR', { month: 'short' })}</p>
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="text-sm text-white font-semibold">{d.date ? formatDateShort(d.date) : ''}</p>
                        <p className="text-xs text-zinc-400 truncate flex items-center gap-1">
                          <MapPin size={10} /> {d.venue}{d.city ? `, ${d.city}` : ''}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {d.time && <span className="text-xs text-zinc-400 flex items-center gap-1"><Clock size={10} /> {d.time}</span>}
                      <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold border ${status.cls}`}>{status.label}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function formatDateShort(dateStr) {
  try {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' });
  } catch { return dateStr; }
}

export default function EventsPage({ onBack }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [flyerEvent, setFlyerEvent] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminCode, setAdminCode] = useState('');
  const [editingEvent, setEditingEvent] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  const fetchEvents = () => {
    fetch(`${API}/events/programming`)
      .then(r => r.json())
      .then(data => {
        const now = new Date().toISOString().slice(0, 10);
        const sorted = data.sort((a, b) => {
          const aNext = (a.dates || []).find(d => d.date >= now);
          const bNext = (b.dates || []).find(d => d.date >= now);
          if (aNext && !bNext) return -1;
          if (!aNext && bNext) return 1;
          if (aNext && bNext) return aNext.date.localeCompare(bNext.date);
          const aLast = (a.dates || []).slice(-1)[0];
          const bLast = (b.dates || []).slice(-1)[0];
          return (bLast?.date || '').localeCompare(aLast?.date || '');
        });
        setEvents(sorted);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => { fetchEvents(); }, []);

  const handleAdminLogin = () => { if (adminCode === 'kazajah2024') { setIsAdmin(true); setAdminCode(''); } };

  const handleDeleteEvent = async (id) => {
    if (!window.confirm('Supprimer cet evenement ?')) return;
    await fetch(`${API}/admin/events/${id}`, { method: 'DELETE', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ password: 'kazajah2024' }) });
    fetchEvents();
  };

  const handleSaveEvent = async (form, isNew) => {
    const url = isNew ? `${API}/admin/events` : `${API}/admin/events/${form.id || form.event_id}`;
    await fetch(url, { method: isNew ? 'POST' : 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...form, password: 'kazajah2024' }) });
    setEditingEvent(null); setShowAdd(false);
    fetchEvents();
  };

  const now = new Date().toISOString().slice(0, 10);
  const upcomingEvents = events.filter(e => (e.dates || []).some(d => d.date >= now));
  const pastEvents = events.filter(e => !(e.dates || []).some(d => d.date >= now));
  const filteredEvents = filter === 'upcoming' ? upcomingEvents : filter === 'past' ? pastEvents : events;

  return (
    <div className="min-h-screen bg-black text-white pt-28 pb-16" data-testid="events-page">
      <div className="px-4 md:px-8 lg:px-16 max-w-6xl mx-auto">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <button onClick={onBack} data-testid="events-back-button"
            className="text-zinc-400 hover:text-white transition-colors mb-4 inline-flex items-center gap-2 text-sm">
            <ArrowRight className="rotate-180" size={16} /> Retour
          </button>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-yellow-500 via-red-500 to-green-500 flex items-center justify-center">
              <Calendar size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-black">
                <span className="bg-gradient-to-r from-yellow-400 via-red-400 to-green-400 bg-clip-text text-transparent">EVENTS KAZAJAH</span>
              </h1>
              <p className="text-zinc-500 text-xs">{events.length} evenements — {upcomingEvents.length} a venir</p>
            </div>
          </div>
        </motion.div>

        {/* Filters */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {[
            { id: 'all', label: `Tous (${events.length})`, gradient: 'from-yellow-500 to-red-500' },
            { id: 'upcoming', label: `A venir (${upcomingEvents.length})`, gradient: 'from-green-500 to-emerald-500' },
            { id: 'past', label: `Passes (${pastEvents.length})`, gradient: 'from-zinc-500 to-zinc-600' }
          ].map(f => (
            <button key={f.id} onClick={() => setFilter(f.id)} data-testid={`filter-events-${f.id}`}
              className={`px-4 py-2 rounded-full text-xs font-bold transition-all ${
                filter === f.id ? `bg-gradient-to-r ${f.gradient} text-black` : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
              }`}>{f.label}</button>
          ))}
        </div>

        {/* Admin Panel */}
        <div className="mb-6">
          {!isAdmin ? (
            <div className="p-3 rounded-xl bg-zinc-900/40 border border-zinc-800">
              <div className="flex items-center gap-2">
                <Lock size={14} className="text-zinc-500" />
                <input type="password" value={adminCode} onChange={e => setAdminCode(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleAdminLogin()}
                  placeholder="Code admin pour gerer les events" data-testid="events-admin-password"
                  className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-xs focus:border-yellow-500 focus:outline-none" />
                <button onClick={handleAdminLogin} data-testid="events-admin-login"
                  className="px-3 py-2 bg-zinc-800 text-zinc-400 rounded-lg text-xs hover:bg-zinc-700">Connexion</button>
              </div>
            </div>
          ) : (
            <div className="rounded-xl bg-gradient-to-br from-yellow-500/5 to-red-500/5 border border-yellow-500/30 overflow-hidden">
              <div className="p-3 flex items-center justify-between">
                <span className="text-xs font-bold text-green-400 flex items-center gap-1"><CheckCircle size={12} /> Admin Events</span>
                <button onClick={() => setShowAdd(!showAdd)} data-testid="add-event-inline"
                  className="px-3 py-1.5 bg-yellow-500 text-black font-bold rounded-lg text-xs flex items-center gap-1 hover:bg-yellow-400">
                  <Plus size={14} /> Nouvel Evenement
                </button>
              </div>
              <AnimatePresence>
                {(showAdd || editingEvent) && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}>
                    <EventFormInline event={editingEvent} onSave={(f) => handleSaveEvent(f, !editingEvent)} onCancel={() => { setEditingEvent(null); setShowAdd(false); }} />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}
        </div>

        {/* Loading */}
        {loading ? (
          <div className="text-center py-16">
            <Loader2 size={32} className="animate-spin text-yellow-400 mx-auto mb-3" />
            <p className="text-zinc-500">Chargement...</p>
          </div>
        ) : (
          <>
            {/* Upcoming highlight */}
            {filter !== 'past' && upcomingEvents.length > 0 && (
              <div className="mb-8">
                <h2 className="text-lg font-black text-white mb-4 flex items-center gap-2">
                  <Star size={18} className="text-yellow-400" /> Prochains Evenements
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {upcomingEvents.map(event => (
                    <EventCard key={event.id || event.event_id} event={event} isUpcoming={true} onFlyerClick={setFlyerEvent}
                      isAdmin={isAdmin} onEdit={setEditingEvent} onDelete={handleDeleteEvent} />
                  ))}
                </div>
              </div>
            )}

            {/* Past events */}
            {filter !== 'upcoming' && pastEvents.length > 0 && (
              <div>
                {filter === 'all' && upcomingEvents.length > 0 && (
                  <h2 className="text-lg font-black text-white mb-4 flex items-center gap-2 mt-8">
                    <Clock size={18} className="text-zinc-500" /> Evenements Passes
                  </h2>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {pastEvents.map(event => (
                    <EventCard key={event.id || event.event_id} event={event} isUpcoming={false} onFlyerClick={setFlyerEvent}
                      isAdmin={isAdmin} onEdit={setEditingEvent} onDelete={handleDeleteEvent} />
                  ))}
                </div>
              </div>
            )}

            {filteredEvents.length === 0 && (
              <div className="text-center py-16">
                <Calendar size={48} className="text-zinc-600 mx-auto mb-4" />
                <p className="text-zinc-500 font-bold">Aucun evenement</p>
              </div>
            )}
          </>
        )}

        {/* Contact */}
        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
          className="mt-16 p-8 rounded-2xl bg-gradient-to-r from-yellow-500/10 via-red-500/10 to-green-500/10 border border-yellow-500/20 text-center">
          <h3 className="text-xl font-black text-white mb-2">Organiser un evenement ?</h3>
          <p className="text-zinc-400 text-sm mb-4">Contactez KAZAJAH STUDIO pour booker un de nos evenements</p>
          <div className="flex gap-3 justify-center flex-wrap">
            <a href="https://www.facebook.com/labelvernabel/" target="_blank" rel="noopener noreferrer"
              className="px-5 py-2 rounded-full bg-blue-500/20 text-blue-400 border border-blue-500/30 text-sm font-bold hover:bg-blue-500/30 transition-colors">Facebook</a>
            <a href="https://instagram.com/djhallmann" target="_blank" rel="noopener noreferrer"
              className="px-5 py-2 rounded-full bg-pink-500/20 text-pink-400 border border-pink-500/30 text-sm font-bold hover:bg-pink-500/30 transition-colors">Instagram</a>
            <a href="mailto:kazajahstudio@gmail.com"
              className="px-5 py-2 rounded-full bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 text-sm font-bold hover:bg-yellow-500/30 transition-colors">Email</a>
          </div>
        </motion.div>

        <div className="mt-12 text-center">
          <button onClick={onBack} className="text-zinc-400 hover:text-white transition-colors flex items-center gap-2 mx-auto">
            <ArrowRight className="rotate-180" size={18} /> Retour
          </button>
        </div>
      </div>

      {/* Flyer fullscreen modal */}
      <AnimatePresence>
        {flyerEvent && flyerEvent.image && (
          <FlyerModal image={flyerEvent.image} name={flyerEvent.name} onClose={() => setFlyerEvent(null)} />
        )}
      </AnimatePresence>
    </div>
  );
}


/* ─── Inline Event Form ─── */
function EventFormInline({ event, onSave, onCancel }) {
  const [form, setForm] = useState({
    id: event?.id || event?.event_id || '',
    event_id: event?.event_id || event?.id || '',
    name: event?.name || '',
    type: event?.type || '',
    description: event?.description || '',
    dj: event?.dj || '',
    color: event?.color || 'from-yellow-500 to-red-500',
    image: event?.image || '',
    dates: event?.dates || [{ date: '', venue: '', city: '', time: '', status: 'confirmed' }]
  });

  const addDate = () => setForm(p => ({ ...p, dates: [...p.dates, { date: '', venue: '', city: '', time: '', status: 'confirmed' }] }));
  const rmDate = (i) => setForm(p => ({ ...p, dates: p.dates.filter((_, idx) => idx !== i) }));
  const updDate = (i, k, v) => setForm(p => ({ ...p, dates: p.dates.map((d, idx) => idx === i ? { ...d, [k]: v } : d) }));

  return (
    <div className="px-4 pb-4 space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
          className="px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white text-sm" placeholder="Nom de l'evenement *" />
        <input value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}
          className="px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white text-sm" placeholder="Type (House, Elektro Ka...)" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <input value={form.dj} onChange={e => setForm({ ...form, dj: e.target.value })}
          className="px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white text-sm" placeholder="DJs" />
        <input value={form.image} onChange={e => setForm({ ...form, image: e.target.value })}
          className="px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white text-sm" placeholder="URL du flyer" />
      </div>
      {form.image && <img src={form.image} alt="" className="w-24 h-24 rounded-lg object-cover border border-zinc-700" />}
      <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
        className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white text-sm" rows={2} placeholder="Description" />

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-zinc-400 text-xs font-bold">Dates</label>
          <button onClick={addDate} className="text-xs text-yellow-400 hover:text-yellow-300 flex items-center gap-1"><Plus size={12} /> Date</button>
        </div>
        {form.dates.map((d, i) => (
          <div key={i} className="grid grid-cols-5 gap-2 mb-2">
            <input type="date" value={d.date} onChange={e => updDate(i, 'date', e.target.value)}
              className="px-2 py-1.5 bg-zinc-900 border border-zinc-700 rounded-lg text-white text-xs" />
            <input value={d.venue} onChange={e => updDate(i, 'venue', e.target.value)}
              className="px-2 py-1.5 bg-zinc-900 border border-zinc-700 rounded-lg text-white text-xs" placeholder="Lieu" />
            <input value={d.city} onChange={e => updDate(i, 'city', e.target.value)}
              className="px-2 py-1.5 bg-zinc-900 border border-zinc-700 rounded-lg text-white text-xs" placeholder="Ville" />
            <input value={d.time} onChange={e => updDate(i, 'time', e.target.value)}
              className="px-2 py-1.5 bg-zinc-900 border border-zinc-700 rounded-lg text-white text-xs" placeholder="Heure" />
            <div className="flex gap-1">
              <select value={d.status} onChange={e => updDate(i, 'status', e.target.value)}
                className="flex-1 px-1 py-1.5 bg-zinc-900 border border-zinc-700 rounded-lg text-white text-xs">
                <option value="confirmed">Confirme</option>
                <option value="a_confirmer">A confirmer</option>
                <option value="passe">Passe</option>
              </select>
              {form.dates.length > 1 && <button onClick={() => rmDate(i)} className="p-1 text-red-400"><X size={12} /></button>}
            </div>
          </div>
        ))}
      </div>

      <div className="flex gap-2">
        <button onClick={() => onSave(form)} className="px-4 py-2 bg-yellow-500 text-black font-bold rounded-lg text-sm flex items-center gap-1"><Save size={14} /> Sauvegarder</button>
        <button onClick={onCancel} className="px-4 py-2 bg-zinc-700 text-white rounded-lg text-sm flex items-center gap-1"><X size={14} /> Annuler</button>
      </div>
    </div>
  );
}
